import {
  type AddToCartMetadata,
  type CartProduct,
  type CartProductBasedGiftWithPurchase,
  type ImageMedia,
  type RemoveFromCartMetadata,
  type VideoMedia,
} from "@tablescape/state/cart";
import { type useCart } from "@shopify/hydrogen-react";
import { type Cart } from "@shopify/hydrogen-react/storefront-api-types";
import {
  type OPProduct,
  type OPProductVariant,
} from "@root/tablescape/state/product";

/* eslint-disable no-redeclare */
export const enum ThemeMode {
  DARK = "dark",
  LIGHT = "light",
}

export const enum PageModal {
  BENTO_BOX_CARD_MODAL,
  SIZE_GUIDE_MODAL,
  VIDEO_MODAL,
}

export const enum GlobalDrawer {
  COUNTRY_SELECTOR,
  COLLECTION_FILTERS,
  GIFT_CARD_RECIPIENT_FORM,
}

export type CartErrorType =
  | "add"
  | "update"
  | "remove"
  | "network"
  | "out_of_stock"
  | null;

export type Discount = {
  name: string;
  productId: string;
  type?: string;
  value: number;
};

export type ProgressBarThresholdItem = {
  amount: number;
  hasReachedThreshold: boolean;
  index: number;
  isCurrentThreshold: boolean;
  isNextThreshold: boolean;
  isPreviousThreshold: boolean;
  subtitleText: string;
  text: string;
  titleText: string;
};

export type RiseGiftCard = {
  email: string;
  giftCardId: string;
  message: string;
  name: string;
};

export type SelectedOption = {
  name: string;
  value: string;
};

export type SelectedVariant = {
  alternateImage: ImageMedia;
  availableForSale: boolean;
  badgeImage: ImageMedia;
  badgeSale: string | null;
  badgeText: string | null;
  badgeType: string | null;
  compareAtPrice: number | null;
  disableWaitlist: boolean | null;
  giftWithPurchase: CartProductBasedGiftWithPurchase | null;
  handle: string;
  id: string;
  kittedProduct: boolean;
  mainProduct: boolean;
  media: (ImageMedia | VideoMedia)[];
  name: string;
  optionCategory: string | null;
  price: number;
  productId: string;
  productTitle: string;
  selectedOptions: SelectedOption[];
  shippingNote: string | null;
  supplementalProduct: boolean;
  title: string;
  variantBundleAvailableForSale: boolean;
  variantBundleComponentImages: ImageMedia[];
};

export type ProductState = {
  selectedVariant: SelectedVariant;
};

export type TablescapeState = {
  cart: {
    attributes?: ReturnType<typeof useCart>["attributes"];
    bundleProduct?: {
      compareAtPrice: number;
      id: string;
      imageURL: string;
      price: number;
      title: string;
    } | null;
    checkoutUrl?: ReturnType<typeof useCart>["checkoutUrl"];
    cost?: ReturnType<typeof useCart>["cost"];
    id?: Cart["id"];
    lineProducts?: Record<CartProduct["handle"], CartProduct>;
    lines?: ReturnType<typeof useCart>["lines"];
    metadata?: AddToCartMetadata;
    quantity?: number;
    removeMetadata?: RemoveFromCartMetadata;
    toggleCart?: boolean;
    upsellAnalytics?: {
      cartId?: Cart["id"];
      cartLines?: ReturnType<typeof useCart>["lines"];
      position: number;
    };
    upsellProducts?: CartProduct[];
  };
  discounts?: Discount[];
  giftCard?: RiseGiftCard;
  isMainProduct?: boolean;
  isSupplementalProduct?: boolean;
  productHero: {
    product?: OPProduct;
    selectedVariant?: OPProductVariant;
  };
  productSelector: {
    selectedVariantId?: string;
  };
  products?: {
    [key: string]: ProductState;
  };
  selectedOptions?: string[];
  selectedVariant?: SelectedVariant;
  ui: {
    cartDrawerAnimating: boolean;
    cartDrawerOpen: boolean;
    cartDrawerUserToggled: boolean;
    cartError: boolean;
    cartErrorMessage: string | null;
    cartErrorType: CartErrorType;
    cartLineAdding: boolean;
    cartLineRemoving: boolean;
    cartLineUpdating: boolean;
    giftNoteDrawerOpen: boolean;
    giftWithPurchaseDrawerOpen: boolean;
    globalDrawerTarget: GlobalDrawer | null;
    pageModalTarget: PageModal | null;
    productVariantDrawerOpen: boolean;
  };
  videoModal?: {
    sources?: { mimeType: string; url: string }[];
    subtitles?: string;
  };
};

export type Context = {
  emit(event: string, data?: unknown): void;
  getState(): TablescapeState;
  hydrate(data: TablescapeState): void;
  on(
    event: "tbsp:variant-selected",
    callback: ({
      discounts,
      products,
      selectedVariant,
    }: {
      discounts?: Discount[];
      products: TablescapeState["products"];
      selectedVariant: SelectedVariant;
    }) => void
  ): void;
  on(
    event: "tbsp:variant-metadata-loading",
    callback: ({
      fieldset,
      input,
      isMainProduct,
      isSupplementalProduct,
    }: {
      fieldset: HTMLFieldSetElement;
      input: HTMLInputElement;
      isMainProduct: boolean;
      isSupplementalProduct: boolean;
    }) => void
  ): void;
  on(event: "tbsp:variant-metadata-loaded", callback: () => void): void;
  on(
    event: "tbsp:variant-price-updated",
    callback: ({
      discounts,
      products,
      selectedVariant,
    }: {
      discounts?: Discount[];
      products?: {
        [key: string]: ProductState;
      };
      selectedVariant?: SelectedVariant;
    }) => void
  ): void;
  on(
    event: "tbsp:highlighted-upsell-variant-price-updated",
    callback: ({
      selectedVariant,
    }: {
      selectedVariant?: SelectedVariant;
    }) => void
  ): void;
  on(
    event: "tbsp:button-clicked",
    callback: ({ action, target }: { action: string; target: string }) => void
  ): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, callback: (data?: any) => void): void;
};

const state: TablescapeState = {
  cart: {},
  discounts: [],
  productHero: {},
  productSelector: {},
  products: {},
  selectedOptions: [],
  ui: {
    cartDrawerAnimating: false,
    cartDrawerOpen: false,
    cartDrawerUserToggled: false,
    cartError: false,
    cartErrorType: null,
    cartErrorMessage: null,
    cartLineAdding: false,
    cartLineUpdating: false,
    cartLineRemoving: false,
    giftNoteDrawerOpen: false,
    giftWithPurchaseDrawerOpen: false,
    productVariantDrawerOpen: false,
    pageModalTarget: null,
    globalDrawerTarget: null,
  },
};

export function getTablescapeState() {
  const currentState = { ...state };

  return currentState;
}

export type * from "./cart";
export * from "./collection";
export type * from "./product";
export type * from "./productReviews";
export type * from "./productSelector";
export type * from "./productSizeGuide";
export type * from "./typography";
